import * as i0 from '@angular/core';
import { Directive, EventEmitter, ElementRef, Component, ChangeDetectionStrategy, Inject, HostBinding, ViewChild, Input, ContentChildren, Output, HostListener, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject, fromEvent, merge } from 'rxjs';
import { takeUntil, auditTime, map, distinctUntilChanged } from 'rxjs/operators';
import { v4 } from 'uuid';
import * as i1 from '@vg-constellation/angular-16/core';
import { IconComponent } from '@vg-constellation/angular-16/icon';

class TooltipContentDirective {
    constructor(elRef) {
        this.elRef = elRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipContentDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.3", type: TooltipContentDirective, isStandalone: true, selector: "[c11nTooltipContent]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[c11nTooltipContent]',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class TooltipComponent {
    onFocusOut(event) {
        if (this.ariaExpanded && !event.currentTarget.contains(event.relatedTarget) && !this.isMouseDown) {
            this.toggleTooltip();
        }
    }
    onWindowResize(event) {
        this.resizeSubject.next(event);
    }
    ;
    constructor(elRef, cdr, fontReadyService, docEvt, document) {
        this.elRef = elRef;
        this.cdr = cdr;
        this.fontReadyService = fontReadyService;
        this.docEvt = docEvt;
        this.isOnDark = false;
        /** Attach point of tooltip relative to trigger. */
        this.align = 'center';
        /** Direction of tooltip relative to trigger. */
        this.direction = 'below';
        /** Indicate if tooltip should calculate orientation. */
        this.autoOrientation = true;
        /** Apply styles based on background. */
        this.colorMode = 'on-light';
        this.dbTime = 100;
        this._destroy = new Subject();
        // TODO get these values dynamically?? Maybe use tokens
        this.paddingValue = 16;
        this.tooltipOffsetValue = 15;
        this.isMouseDown = false;
        this.tooltipId = 'tooltip-' + v4();
        this.ariaExpanded = false;
        this.isActivated = false;
        this.position = 'c11n-tooltip--' + this.getTooltipPosition();
        this.initializing = true;
        /** Triggers when tooltip state changes. */
        this.stateChange = new EventEmitter();
        this.isInRegion = (x, y, gridKey) => {
            const region = this.viewportGrid[gridKey];
            const vp = region.coordinates;
            if ((x >= vp.x1) && (x <= vp.x2) && (y >= vp.y1) && (y <= vp.y2)) {
                return true;
            }
            return false;
        };
        this.getTriggerGridRegion = (x, y) => {
            const keys = Object.keys(this.viewportGrid);
            const pos = keys.filter(k => {
                return this.isInRegion(x, y, k);
            })[0];
            return pos;
        };
        this.initializeTooltip = () => {
            const contentProperties = this.contentElement.getBoundingClientRect();
            this.contentWidth = contentProperties.width;
            this.contentHeight = contentProperties.height;
            this.cdr.detectChanges();
            this.scrollAndResizeHandler(this.getTooltipPosition());
            this.initializing = false;
        };
        this.isAtAboveCenterPosition = (x, y) => {
            if (this.fitsOnTop(y) && this.fitsHorizontallyCentered(x)) {
                return 'above-center';
            }
            return false;
        };
        this.isAtAboveStartPosition = (x, y) => {
            if (this.fitsOnTop(y) && this.fitsAlignedToRight(x)) {
                return 'above-start';
            }
            return false;
        };
        this.isAtRightEndPosition = (x, y) => {
            if (this.fitsOnRight(x) && this.fitsAlignedToTop(y)) {
                return 'right-end';
            }
            return false;
        };
        this.isAtRightCenterPosition = (x, y) => {
            if (this.fitsOnRight(x) && this.fitsVerticallyCentered(y)) {
                return 'right-center';
            }
            return false;
        };
        this.isAtRightStartPosition = (x, y) => {
            if (this.fitsOnRight(x) && this.fitsAlignedToBottom(y)) {
                return 'right-start';
            }
            return false;
        };
        this.isAtBelowStartPosition = (x, y) => {
            if (this.fitsOnBottom(y) && this.fitsAlignedToRight(x)) {
                return 'below-start';
            }
            return false;
        };
        this.isAtBelowCenterPosition = (x, y) => {
            if (this.fitsOnBottom(y) && this.fitsHorizontallyCentered(x)) {
                return 'below-center';
            }
            return false;
        };
        this.isAtBelowEndPosition = (x, y) => {
            if (this.fitsOnBottom(y) && this.fitsAlignedToLeft(x)) {
                return 'below-end';
            }
            return false;
        };
        this.isAtLeftStartPosition = (x, y) => {
            if (this.fitsOnLeft(x) && this.fitsAlignedToBottom(y)) {
                return 'left-start';
            }
            return false;
        };
        this.isAtLeftCenterPosition = (x, y) => {
            if (this.fitsOnLeft(x) && this.fitsVerticallyCentered(y)) {
                return 'left-center';
            }
            return false;
        };
        this.isAtLeftEndPosition = (x, y) => {
            if (this.fitsOnLeft(x) && this.fitsAlignedToTop(y)) {
                return 'left-end';
            }
            return false;
        };
        this.isAtAboveEndPosition = (x, y) => {
            if (this.fitsOnTop(y) && this.fitsAlignedToLeft(x)) {
                return 'above-end';
            }
            return false;
        };
        this.scrollAndResizeHandler = (position) => {
            this.position = 'c11n-tooltip--' + position;
            this.cdr.detectChanges();
        };
        this.document = document;
        this.resizeSubject = new Subject();
    }
    ngOnInit() {
        this.defaultPosition = this.getDefaultPosition();
        this.isOnDark = this.colorMode === 'on-dark';
        const defaultCoordinates = {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 0
        };
        this.docEvt.getEventObs('keyup').pipe(takeUntil(this._destroy)).subscribe(event => {
            this.keyUpHandler(event);
        });
        this.viewportGrid = {
            topLeft: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtBelowCenterPosition, this.isAtBelowStartPosition, this.isAtRightStartPosition]
            },
            top: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtBelowCenterPosition, this.isAtBelowStartPosition, this.isAtBelowEndPosition, this.isAtRightStartPosition, this.isAtLeftStartPosition],
            },
            topRight: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtBelowCenterPosition, this.isAtBelowEndPosition, this.isAtLeftStartPosition],
            },
            left: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtBelowCenterPosition, this.isAtAboveStartPosition, this.isAtBelowStartPosition, this.isAtRightEndPosition, this.isAtRightStartPosition, this.isAtRightCenterPosition],
            },
            middle: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtAboveStartPosition, this.isAtAboveEndPosition, this.isAtBelowCenterPosition, this.isAtBelowStartPosition, this.isAtBelowEndPosition, this.isAtRightEndPosition, this.isAtLeftEndPosition, this.isAtRightCenterPosition, this.isAtLeftCenterPosition, this.isAtRightStartPosition, this.isAtLeftStartPosition],
            },
            right: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtBelowCenterPosition, this.isAtAboveEndPosition, this.isAtBelowEndPosition, this.isAtLeftEndPosition, this.isAtLeftStartPosition, this.isAtLeftCenterPosition],
            },
            bottomLeft: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtAboveStartPosition, this.isAtRightEndPosition],
            },
            bottom: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtAboveStartPosition, this.isAtAboveEndPosition, this.isAtRightEndPosition, this.isAtLeftEndPosition],
            },
            bottomRight: {
                coordinates: defaultCoordinates,
                callbackMethods: [this.isAtAboveCenterPosition, this.isAtAboveEndPosition, this.isAtLeftEndPosition],
            }
        };
        this.positionToMethodMap = {
            'above-center': this.isAtAboveCenterPosition,
            'above-start': this.isAtAboveStartPosition,
            'right-end': this.isAtRightEndPosition,
            'right-center': this.isAtRightCenterPosition,
            'right-start': this.isAtRightStartPosition,
            'below-start': this.isAtBelowStartPosition,
            'below-center': this.isAtBelowCenterPosition,
            'below-end': this.isAtBelowEndPosition,
            'left-start': this.isAtLeftStartPosition,
            'left-center': this.isAtLeftCenterPosition,
            'left-end': this.isAtLeftEndPosition,
            'above-end': this.isAtAboveEndPosition
        };
        this.getRecalcObs().pipe(takeUntil(this._destroy)).subscribe((position) => {
            this.scrollAndResizeHandler(position);
        });
        this.scrollAndResizeHandler(this.getTooltipPosition());
        this.cdr.detectChanges();
    }
    ngAfterContentInit() {
        this.contentList.changes.pipe(takeUntil(this._destroy)).subscribe((list) => {
            this.updateContent(list.first);
        });
        this.updateContent(this.contentList.first);
        this.cdr.detectChanges();
    }
    async ngAfterViewInit() {
        this.setLabelVariables();
        await this.fontReadyService.fontReady();
        this.initializeTooltip();
    }
    getDefaultPosition() {
        return (this.direction + '-' + this.align);
    }
    ngOnChanges(changes) {
        Object.keys(changes)
            .filter(chg => ['colorMode', 'align', 'direction', 'autoOrientation', 'labelText', 'contextRef'].includes(chg))
            .forEach(key => {
            if (!changes[key].isFirstChange()) {
                switch (key) {
                    case 'align':
                    case 'direction':
                        this.defaultPosition = this.getDefaultPosition();
                        this.scrollAndResizeHandler(this.getTooltipPosition());
                        break;
                    case 'autoOrientation':
                        this.scrollAndResizeHandler(this.getTooltipPosition());
                        break;
                    case 'contextRef':
                        this.scrollAndResizeHandler(this.getTooltipPosition());
                        break;
                    case 'colorMode':
                        this.isOnDark = this.colorMode === 'on-dark';
                        break;
                    case 'labelText':
                        this.setLabelVariables();
                        break;
                }
            }
        });
    }
    handleSetTooltipPosition() {
        setTimeout(() => this.scrollAndResizeHandler(this.getTooltipPosition()), 500);
    }
    getRecalcObs() {
        const resizeObs = this.resizeSubject.asObservable();
        const scrollObs = fromEvent(this.document, 'scroll');
        return merge(resizeObs, scrollObs).pipe(auditTime(this.dbTime), map(() => this.getTooltipPosition()), distinctUntilChanged());
    }
    updateContent(content) {
        if (content) {
            this.contentElement = content.elRef.nativeElement;
        }
    }
    getTooltipPosition() {
        if (this.triggerElRef?.nativeElement && this.contentElement && this.autoOrientation) {
            this.viewportGrid.topLeft.coordinates = this.getRegionCoordinates(1, 1);
            this.viewportGrid.top.coordinates = this.getRegionCoordinates(2, 1);
            this.viewportGrid.topRight.coordinates = this.getRegionCoordinates(3, 1);
            this.viewportGrid.left.coordinates = this.getRegionCoordinates(1, 2);
            this.viewportGrid.middle.coordinates = this.getRegionCoordinates(2, 2);
            this.viewportGrid.right.coordinates = this.getRegionCoordinates(3, 2);
            this.viewportGrid.bottomLeft.coordinates = this.getRegionCoordinates(1, 3);
            this.viewportGrid.bottom.coordinates = this.getRegionCoordinates(2, 3);
            this.viewportGrid.bottomRight.coordinates = this.getRegionCoordinates(3, 3);
            const triggerProperties = this.triggerElRef.nativeElement.getBoundingClientRect();
            const triggerX = triggerProperties['x'] + triggerProperties['width'] / 2;
            const triggerY = triggerProperties['y'] + triggerProperties['height'] / 2;
            const triggerGridRegion = this.getTriggerGridRegion(triggerX, triggerY);
            if (triggerGridRegion !== undefined) {
                const callbackMethods = [this.positionToMethodMap[this.defaultPosition], ...this.viewportGrid[triggerGridRegion].callbackMethods];
                for (let i = 0; i < callbackMethods.length; i++) {
                    const callbackResult = callbackMethods[i](triggerX, triggerY);
                    if (callbackResult) {
                        return callbackResult;
                    }
                }
            }
            if (this.fitsAlignedToRight(triggerX)) {
                return 'below-start';
            }
            if (this.fitsAlignedToLeft(triggerX)) {
                return 'below-end';
            }
            return 'below-center';
        }
        return this.defaultPosition;
    }
    getContextWidth() {
        if (this.contextRef) {
            const { x, width } = this.contextRef.getBoundingClientRect();
            return x + width;
        }
        else {
            return this.document.documentElement.clientWidth;
        }
    }
    getContextHeight() {
        if (this.contextRef) {
            const { y, height } = this.contextRef.getBoundingClientRect();
            return y + height;
        }
        else {
            return this.document.documentElement.clientHeight;
        }
    }
    getRegionCoordinates(columnNumber, rowNumber) {
        const contextHeight = this.getContextHeight();
        const contextWidth = this.getContextWidth();
        const numberOfCells = 3;
        return {
            x1: contextWidth / numberOfCells * (columnNumber - 1),
            x2: contextWidth / numberOfCells * columnNumber - (columnNumber === numberOfCells ? 0 : 1),
            y1: contextHeight / numberOfCells * (rowNumber - 1),
            y2: contextHeight / numberOfCells * rowNumber - (rowNumber === numberOfCells ? 0 : 1)
        };
    }
    fitsOnTop(y) {
        if (
        // The y value is the vertical center of the tooltip trigger
        y -
            // the tooltipOffsetValue is the distance from the center of the trigger to the edge of the tooltip
            this.tooltipOffsetValue -
            // the paddingValue is the padding around the tooltip content, in this case the top
            this.paddingValue -
            // this value is the height of the content only
            this.contentHeight -
            // the paddingValue is the padding around the tooltip content, in this case the bottom
            this.paddingValue
            // this value is the top of the viewport
            >= 0) {
            return true;
        }
        return false;
    }
    fitsOnRight(x) {
        if (
        // The x is the horizontal center of the tooltip trigger
        x +
            // the tooltipOffsetValue is the distance from the center of the trigger to the edge of the tooltip
            this.tooltipOffsetValue +
            // the paddingValue is the padding around the tooltip content, in this case the left
            this.paddingValue +
            // this value is the width of the content only
            this.contentWidth +
            // the paddingValue is the padding around the tooltip content, in this case the right
            this.paddingValue
            // this value is the height of the viewport
            <= this.viewportGrid.right.coordinates.x2) {
            return true;
        }
        return false;
    }
    fitsOnBottom(y) {
        if (
        // The y value is the vertical center of the tooltip trigger
        y +
            // the tooltipOffsetValue is the distance from the center of the trigger to the edge of the tooltip
            this.tooltipOffsetValue +
            // the paddingValue is the padding around the tooltip content, in this case the top
            this.paddingValue +
            // this value is the height of the content only
            this.contentHeight +
            // the paddingValue is the padding around the tooltip content, in this case the bottom
            this.paddingValue
            // this value is the height of the viewport
            <= this.viewportGrid.bottom.coordinates.y2) {
            return true;
        }
        return false;
    }
    fitsOnLeft(x) {
        if (
        // The x value is the horizontal center of the tooltip trigger
        x -
            // the tooltipOffsetValue is the distance from the center of the trigger to the edge of the tooltip
            this.tooltipOffsetValue -
            // the paddingValue is the padding around the tooltip content, in this case the right
            this.paddingValue -
            // this value is the width of the content only
            this.contentWidth -
            // the paddingValue is the padding around the tooltip content, in this case the left
            this.paddingValue
            // not over the left edge of viewport
            >= 0) {
            return true;
        }
        return false;
    }
    fitsAlignedToTop(y) {
        if (y -
            // since it sits to the top we use the entire hright of the content
            this.contentHeight -
            // padding on top
            this.paddingValue
            // not over the top edge of viewport
            >= 0) {
            return true;
        }
        return false;
    }
    fitsAlignedToRight(x) {
        if (x +
            // since it sits to the right we use the entire width of the content
            this.contentWidth +
            // padding on right
            this.paddingValue
            // not over the right edge of viewport
            <= this.viewportGrid.right.coordinates.x2) {
            return true;
        }
        return false;
    }
    fitsAlignedToBottom(y) {
        if (y +
            // since it sits to the bottom we use the entire height of the content
            this.contentHeight +
            // padding on bottom
            this.paddingValue
            // not over the bottom edge of viewport
            <= this.viewportGrid.bottom.coordinates.y2) {
            return true;
        }
        return false;
    }
    fitsAlignedToLeft(x) {
        if (x -
            // since it sits to the left we use the entire width of the content
            this.contentWidth -
            // padding on left
            this.paddingValue
            // not over the left edge of viewport
            >= 0) {
            return true;
        }
        return false;
    }
    fitsHorizontallyCentered(x) {
        if ((x -
            // since it's centered we only use half the width of the content
            this.contentWidth / 2 -
            // padding on left
            this.paddingValue
            // not over the left edge of viewport
            >= 0) && (x +
            // since it's centered we only use half the width of the content
            this.contentWidth / 2 +
            // padding on right
            this.paddingValue
            // not over the right edge of viewport
            <= this.viewportGrid.right.coordinates.x2)) {
            return true;
        }
        return false;
    }
    fitsVerticallyCentered(y) {
        if ((y -
            // since it's centered we only use half the height of the content
            this.contentHeight / 2 -
            // padding on top
            this.paddingValue
            // not over the top edge of viewport
            >= 0) && (y +
            // since it's centered we only use half the height of the content
            this.contentHeight / 2 +
            // padding on bottom
            this.paddingValue
            // not over the bottom edge of viewport
            <= this.viewportGrid.bottom.coordinates.y2)) {
            return true;
        }
        return false;
    }
    setLabelVariables() {
        let captureGroups;
        try {
            captureGroups = this.labelText.match(/(.*?)((\S|\u00A0)+(\s*)$)/) || [];
            this.labelLastWord = captureGroups[2];
            this.labelAllButLastWord = captureGroups[1];
        }
        catch (e) {
            this.labelLastWord = this.labelText;
            this.labelAllButLastWord = '';
        }
        this.cdr.detectChanges();
    }
    toggleTooltip() {
        this.ariaExpanded = !this.ariaExpanded;
        this.isActivated = this.ariaExpanded;
        this.cdr.detectChanges();
        this.stateChange.emit({ isActive: this.isActivated, componentRef: this });
    }
    keyUpHandler(event) {
        if (event.key === 'Escape') {
            if (this.ariaExpanded) {
                this.toggleTooltip();
            }
        }
    }
    ;
    mouseenterHandler(event) {
        // instead of mouseenter, we're using pointerenter here because it allows us
        // to prevent mouseenterHandler from getting fired with click event on touch devices
        if (!this.ariaExpanded && event.pointerType === 'mouse') {
            this.toggleTooltip();
        }
    }
    mouseleaveHandler() {
        if (this.ariaExpanded) {
            this.toggleTooltip();
        }
    }
    onMouseDown() {
        this.isMouseDown = true;
    }
    onMouseUp() {
        this.isMouseDown = false;
    }
    ngOnDestroy() {
        this._destroy.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1.FontReadyService }, { token: i1.DocumentEventService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.3", type: TooltipComponent, isStandalone: true, selector: "c11n-tooltip", inputs: { align: "align", direction: "direction", autoOrientation: "autoOrientation", colorMode: "colorMode", labelText: "labelText", contextRef: "contextRef" }, outputs: { stateChange: "stateChange" }, host: { listeners: { "focusout": "onFocusOut($event)", "window:resize": "onWindowResize($event)" }, properties: { "class.c11n-tooltip--on-dark": "this.isOnDark", "class.c11n-tooltip--activated": "this.isActivated", "class": "this.position", "class.c11n-tooltip--initializing": "this.initializing" }, classAttribute: "c11n-tooltip" }, queries: [{ propertyName: "contentList", predicate: TooltipContentDirective, descendants: true }], viewQueries: [{ propertyName: "triggerElRef", first: true, predicate: ["trigger"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: "{{ labelAllButLastWord }}\n<span class=\"c11n-tooltip__last-word-and-tooltip\">\n  <span class=\"c11n-tooltip__label\">{{ labelLastWord }}</span>\n  <span\n    class=\"c11n-tooltip__inner-wrapper\"\n    (pointerenter)=\"mouseenterHandler($event)\"\n    (mouseleave)=\"mouseleaveHandler()\"\n    #toggleableArea\n  >\n    <button\n      type=\"button\"\n      class=\"c11n-tooltip__trigger\"\n      [attr.aria-expanded]=\"ariaExpanded\"\n      [attr.aria-label]=\"labelText + ' help tooltip'\"\n      [attr.aria-describedby]=\"ariaExpanded ? tooltipId : null\"\n      (mousedown)=\"onMouseDown()\"\n      (mouseup)=\"onMouseUp()\"\n      (click)=\"toggleTooltip()\"\n      #trigger\n    >\n      <c11n-icon name=\"help\" size=\"small\" aria-hidden=\"true\"></c11n-icon>\n    </button>\n    <span class=\"c11n-tooltip__popup\" tabindex=\"-1\">\n      <span class=\"c11n-tooltip__pointer\">\n        <svg viewBox=\"0 0 16 6\" focusable=\"false\" aria-hidden=\"true\">\n          <polygon points=\"8 2 14 6 15.8027954 6 14.5546875 5.1679077 8.5546875 1.1679077 8 0.7981567 7.4453125 1.1679077 1.4453125 5.1679077 0.1972046 6 2 6\"></polygon>\n          <polygon points=\"2 6 14 6 8 2\"></polygon>\n        </svg>\n      </span>\n      <span [attr.id]=\"tooltipId\" class=\"c11n-tooltip__content\" role=\"tooltip\" [attr.aria-hidden]=\"!ariaExpanded\">\n        <ng-content select=\"[c11nTooltipContent]\"></ng-content>\n      </span>\n    </span>\n  </span>\n</span>\n", styles: [":host(.c11n-tooltip) .c11n-tooltip__last-word-and-tooltip{position:relative;padding-right:1.6875rem;height:1.5rem;white-space:nowrap;z-index:797;transition-property:z-index;transition-duration:0ms;transition-delay:.2s}:host(.c11n-tooltip) .c11n-tooltip__inner-wrapper{position:absolute;display:inline-block;top:50%;right:0;margin-top:-.75rem;transform:translateY(.125rem);white-space:normal}:host(.c11n-tooltip) .c11n-tooltip__trigger{background:transparent;border:none;color:inherit;cursor:default;padding:.3125rem;margin:0;border-radius:50%;position:relative;z-index:801}:host(.c11n-tooltip) .c11n-tooltip__trigger:before{content:\"\";position:absolute;background-color:#0405051a;border-radius:50%;transform:scale(0) translateZ(0);transform-origin:center;transition-property:transform,background-color;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.1s;transform-style:flat;backface-visibility:hidden;top:0;left:0;width:100%;height:100%}.c11n--on-dark :host(.c11n-tooltip) .c11n-tooltip__trigger:before{background-color:#ffffff4d}:host(.c11n-tooltip) .c11n-tooltip__trigger:hover:before{transform:scale(1) translateZ(0);transition-duration:.1s}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:before{transform:scale(1) translateZ(0);transition-duration:.1s}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:before{transition-property:none}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible{position:relative;outline:solid .125rem #ffffff;outline-offset:.25rem}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:after{content:\"\";display:block;position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;border-radius:inherit;pointer-events:none;outline:solid .125rem #0f62c5;outline-offset:.125rem}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible{z-index:auto}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg{display:block;pointer-events:none;width:.875rem;height:.875rem}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg.c11n-icon--smaller{width:.5rem;height:.5rem}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg use{fill:#040505}:host(.c11n-tooltip) .c11n-tooltip__trigger[aria-expanded=true]+.c11n-tooltip__popup{opacity:1;pointer-events:initial;visibility:visible;transition-duration:.25s;transition-delay:0ms,0ms}:host(.c11n-tooltip) .c11n-tooltip__popup{position:absolute;z-index:800;box-shadow:0 0 .25rem .0625rem #0405050f,0 .0625rem .0625rem #0405050f,0 .125rem .25rem #04050514,0 .25rem .5rem #0405051f;max-width:15rem;border-radius:.125rem;width:max-content;opacity:0;pointer-events:none;visibility:hidden;transition-property:opacity,visibility;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.2s;transition-delay:0ms,.2s;transform-style:flat;backface-visibility:hidden;transform:translateZ(0);outline:.0625rem solid transparent;text-align:left}:host(.c11n-tooltip) .c11n-tooltip__popup:after{content:\"\";position:absolute}:host(.c11n-tooltip) .c11n-tooltip__popup:focus{outline:none}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer{position:absolute;z-index:798;width:0;height:0;overflow:visible;transform-origin:center;display:block}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg{display:block;position:absolute;left:-.5rem;top:-.3125rem;width:1rem;height:.375rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:first-of-type{fill:#fff}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:last-of-type{fill:#3f4444}:host(.c11n-tooltip) .c11n-tooltip__popup:before{box-shadow:0 0 0 .0625rem #fff;z-index:797}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content{color:#fff;background:#3f4444;padding:1rem;box-shadow:inset 0 0 0 .0625rem #fff;border-radius:.125rem;position:relative;display:block}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*{font-family:FF Mark,Arial,sans-serif;font-weight:400;font-style:normal;font-size:1.0625rem;line-height:1.4117647059}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:before,:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:after{content:\"\";display:block;height:0;width:0}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:before{margin-bottom:-.4375rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:after{margin-top:-.3125rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content :last-child{margin-bottom:0}:host(.c11n-tooltip.c11n-tooltip--activated) .c11n-tooltip__last-word-and-tooltip{z-index:798;transition-delay:0ms}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__trigger:before{background-color:#ffffff4d}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__trigger .c11n-icon svg use{fill:#fff!important}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__popup .c11n-tooltip__content{color:#040505;background:#fff}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:last-of-type{fill:#fff}:host(.c11n-tooltip.c11n-tooltip--dismissed) .c11n-tooltip__popup{opacity:0!important;pointer-events:none!important;visibility:hidden!important;transition-delay:0ms,.2s}:host(.c11n-tooltip.c11n-tooltip--initializing) .c11n-tooltip__popup{display:block!important}:host(.c11n-tooltip.c11n-tooltip--position-none) .c11n-tooltip__popup{display:none}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__popup{bottom:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__popup:after{left:0;height:1.6875rem;bottom:-1.6875rem;width:100%}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__pointer{bottom:0;transform:rotate(180deg)}:host(.c11n-tooltip.c11n-tooltip--below-start,.c11n-tooltip.c11n-tooltip--below-center,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup{top:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--below-start,.c11n-tooltip.c11n-tooltip--below-center,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup:after{left:0;height:1.6875rem;top:-1.6875rem;width:100%}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--below-start) .c11n-tooltip__popup{left:0;transform:translate(-.25rem)}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--below-start) .c11n-tooltip__pointer{left:1rem}:host(.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--below-center) .c11n-tooltip__popup{left:0;transform:translate(calc(-50% + .75rem))}:host(.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--below-center) .c11n-tooltip__pointer{left:50%}:host(.c11n-tooltip.c11n-tooltip--above-end,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup{right:0;transform:translate(.25rem)}:host(.c11n-tooltip.c11n-tooltip--above-end,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__pointer{right:1rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__popup{right:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__popup:after{right:-1.6875rem;height:100%;top:0;width:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__pointer{right:0;transform:rotate(90deg)}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup{left:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup:after{left:-1.6875rem;height:100%;top:0;width:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__pointer{left:0;transform:rotate(-90deg)}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--right-start) .c11n-tooltip__popup{top:0;transform:translateY(-.25rem)}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--right-start) .c11n-tooltip__pointer{top:1rem}:host(.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--right-center) .c11n-tooltip__popup{top:0;transform:translateY(calc(-50% + .75rem))}:host(.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--right-center) .c11n-tooltip__pointer{top:50%}:host(.c11n-tooltip.c11n-tooltip--left-end,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup{bottom:0;transform:translateY(.25rem)}:host(.c11n-tooltip.c11n-tooltip--left-end,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__pointer{bottom:1rem}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "c11n-icon", inputs: ["size", "scale", "name", "altText"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c11n-tooltip', changeDetection: ChangeDetectionStrategy.OnPush, host: { class: 'c11n-tooltip' }, standalone: true, imports: [IconComponent, TooltipContentDirective], template: "{{ labelAllButLastWord }}\n<span class=\"c11n-tooltip__last-word-and-tooltip\">\n  <span class=\"c11n-tooltip__label\">{{ labelLastWord }}</span>\n  <span\n    class=\"c11n-tooltip__inner-wrapper\"\n    (pointerenter)=\"mouseenterHandler($event)\"\n    (mouseleave)=\"mouseleaveHandler()\"\n    #toggleableArea\n  >\n    <button\n      type=\"button\"\n      class=\"c11n-tooltip__trigger\"\n      [attr.aria-expanded]=\"ariaExpanded\"\n      [attr.aria-label]=\"labelText + ' help tooltip'\"\n      [attr.aria-describedby]=\"ariaExpanded ? tooltipId : null\"\n      (mousedown)=\"onMouseDown()\"\n      (mouseup)=\"onMouseUp()\"\n      (click)=\"toggleTooltip()\"\n      #trigger\n    >\n      <c11n-icon name=\"help\" size=\"small\" aria-hidden=\"true\"></c11n-icon>\n    </button>\n    <span class=\"c11n-tooltip__popup\" tabindex=\"-1\">\n      <span class=\"c11n-tooltip__pointer\">\n        <svg viewBox=\"0 0 16 6\" focusable=\"false\" aria-hidden=\"true\">\n          <polygon points=\"8 2 14 6 15.8027954 6 14.5546875 5.1679077 8.5546875 1.1679077 8 0.7981567 7.4453125 1.1679077 1.4453125 5.1679077 0.1972046 6 2 6\"></polygon>\n          <polygon points=\"2 6 14 6 8 2\"></polygon>\n        </svg>\n      </span>\n      <span [attr.id]=\"tooltipId\" class=\"c11n-tooltip__content\" role=\"tooltip\" [attr.aria-hidden]=\"!ariaExpanded\">\n        <ng-content select=\"[c11nTooltipContent]\"></ng-content>\n      </span>\n    </span>\n  </span>\n</span>\n", styles: [":host(.c11n-tooltip) .c11n-tooltip__last-word-and-tooltip{position:relative;padding-right:1.6875rem;height:1.5rem;white-space:nowrap;z-index:797;transition-property:z-index;transition-duration:0ms;transition-delay:.2s}:host(.c11n-tooltip) .c11n-tooltip__inner-wrapper{position:absolute;display:inline-block;top:50%;right:0;margin-top:-.75rem;transform:translateY(.125rem);white-space:normal}:host(.c11n-tooltip) .c11n-tooltip__trigger{background:transparent;border:none;color:inherit;cursor:default;padding:.3125rem;margin:0;border-radius:50%;position:relative;z-index:801}:host(.c11n-tooltip) .c11n-tooltip__trigger:before{content:\"\";position:absolute;background-color:#0405051a;border-radius:50%;transform:scale(0) translateZ(0);transform-origin:center;transition-property:transform,background-color;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.1s;transform-style:flat;backface-visibility:hidden;top:0;left:0;width:100%;height:100%}.c11n--on-dark :host(.c11n-tooltip) .c11n-tooltip__trigger:before{background-color:#ffffff4d}:host(.c11n-tooltip) .c11n-tooltip__trigger:hover:before{transform:scale(1) translateZ(0);transition-duration:.1s}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:before{transform:scale(1) translateZ(0);transition-duration:.1s}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:before{transition-property:none}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible{position:relative;outline:solid .125rem #ffffff;outline-offset:.25rem}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible:after{content:\"\";display:block;position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;border-radius:inherit;pointer-events:none;outline:solid .125rem #0f62c5;outline-offset:.125rem}:host(.c11n-tooltip) .c11n-tooltip__trigger:focus-visible{z-index:auto}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg{display:block;pointer-events:none;width:.875rem;height:.875rem}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg.c11n-icon--smaller{width:.5rem;height:.5rem}:host(.c11n-tooltip) .c11n-tooltip__trigger .c11n-icon svg use{fill:#040505}:host(.c11n-tooltip) .c11n-tooltip__trigger[aria-expanded=true]+.c11n-tooltip__popup{opacity:1;pointer-events:initial;visibility:visible;transition-duration:.25s;transition-delay:0ms,0ms}:host(.c11n-tooltip) .c11n-tooltip__popup{position:absolute;z-index:800;box-shadow:0 0 .25rem .0625rem #0405050f,0 .0625rem .0625rem #0405050f,0 .125rem .25rem #04050514,0 .25rem .5rem #0405051f;max-width:15rem;border-radius:.125rem;width:max-content;opacity:0;pointer-events:none;visibility:hidden;transition-property:opacity,visibility;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.2s;transition-delay:0ms,.2s;transform-style:flat;backface-visibility:hidden;transform:translateZ(0);outline:.0625rem solid transparent;text-align:left}:host(.c11n-tooltip) .c11n-tooltip__popup:after{content:\"\";position:absolute}:host(.c11n-tooltip) .c11n-tooltip__popup:focus{outline:none}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer{position:absolute;z-index:798;width:0;height:0;overflow:visible;transform-origin:center;display:block}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg{display:block;position:absolute;left:-.5rem;top:-.3125rem;width:1rem;height:.375rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:first-of-type{fill:#fff}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:last-of-type{fill:#3f4444}:host(.c11n-tooltip) .c11n-tooltip__popup:before{box-shadow:0 0 0 .0625rem #fff;z-index:797}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content{color:#fff;background:#3f4444;padding:1rem;box-shadow:inset 0 0 0 .0625rem #fff;border-radius:.125rem;position:relative;display:block}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*{font-family:FF Mark,Arial,sans-serif;font-weight:400;font-style:normal;font-size:1.0625rem;line-height:1.4117647059}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:before,:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:after{content:\"\";display:block;height:0;width:0}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:before{margin-bottom:-.4375rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content>*:after{margin-top:-.3125rem}:host(.c11n-tooltip) .c11n-tooltip__popup .c11n-tooltip__content :last-child{margin-bottom:0}:host(.c11n-tooltip.c11n-tooltip--activated) .c11n-tooltip__last-word-and-tooltip{z-index:798;transition-delay:0ms}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__trigger:before{background-color:#ffffff4d}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__trigger .c11n-icon svg use{fill:#fff!important}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__popup .c11n-tooltip__content{color:#040505;background:#fff}:host(.c11n-tooltip.c11n-tooltip--on-dark) .c11n-tooltip__popup .c11n-tooltip__pointer svg polygon:last-of-type{fill:#fff}:host(.c11n-tooltip.c11n-tooltip--dismissed) .c11n-tooltip__popup{opacity:0!important;pointer-events:none!important;visibility:hidden!important;transition-delay:0ms,.2s}:host(.c11n-tooltip.c11n-tooltip--initializing) .c11n-tooltip__popup{display:block!important}:host(.c11n-tooltip.c11n-tooltip--position-none) .c11n-tooltip__popup{display:none}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__popup{bottom:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__popup:after{left:0;height:1.6875rem;bottom:-1.6875rem;width:100%}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--above-end) .c11n-tooltip__pointer{bottom:0;transform:rotate(180deg)}:host(.c11n-tooltip.c11n-tooltip--below-start,.c11n-tooltip.c11n-tooltip--below-center,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup{top:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--below-start,.c11n-tooltip.c11n-tooltip--below-center,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup:after{left:0;height:1.6875rem;top:-1.6875rem;width:100%}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--below-start) .c11n-tooltip__popup{left:0;transform:translate(-.25rem)}:host(.c11n-tooltip.c11n-tooltip--above-start,.c11n-tooltip.c11n-tooltip--below-start) .c11n-tooltip__pointer{left:1rem}:host(.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--below-center) .c11n-tooltip__popup{left:0;transform:translate(calc(-50% + .75rem))}:host(.c11n-tooltip.c11n-tooltip--above-center,.c11n-tooltip.c11n-tooltip--below-center) .c11n-tooltip__pointer{left:50%}:host(.c11n-tooltip.c11n-tooltip--above-end,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__popup{right:0;transform:translate(.25rem)}:host(.c11n-tooltip.c11n-tooltip--above-end,.c11n-tooltip.c11n-tooltip--below-end) .c11n-tooltip__pointer{right:1rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__popup{right:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__popup:after{right:-1.6875rem;height:100%;top:0;width:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--left-end) .c11n-tooltip__pointer{right:0;transform:rotate(90deg)}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup{left:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup:after{left:-1.6875rem;height:100%;top:0;width:1.6875rem}:host(.c11n-tooltip.c11n-tooltip--right-start,.c11n-tooltip.c11n-tooltip--right-center,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__pointer{left:0;transform:rotate(-90deg)}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--right-start) .c11n-tooltip__popup{top:0;transform:translateY(-.25rem)}:host(.c11n-tooltip.c11n-tooltip--left-start,.c11n-tooltip.c11n-tooltip--right-start) .c11n-tooltip__pointer{top:1rem}:host(.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--right-center) .c11n-tooltip__popup{top:0;transform:translateY(calc(-50% + .75rem))}:host(.c11n-tooltip.c11n-tooltip--left-center,.c11n-tooltip.c11n-tooltip--right-center) .c11n-tooltip__pointer{top:50%}:host(.c11n-tooltip.c11n-tooltip--left-end,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__popup{bottom:0;transform:translateY(.25rem)}:host(.c11n-tooltip.c11n-tooltip--left-end,.c11n-tooltip.c11n-tooltip--right-end) .c11n-tooltip__pointer{bottom:1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1.FontReadyService }, { type: i1.DocumentEventService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { isOnDark: [{
                type: HostBinding,
                args: ['class.c11n-tooltip--on-dark']
            }], triggerElRef: [{
                type: ViewChild,
                args: ['trigger', { read: ElementRef }]
            }], align: [{
                type: Input
            }], direction: [{
                type: Input
            }], autoOrientation: [{
                type: Input
            }], colorMode: [{
                type: Input
            }], labelText: [{
                type: Input
            }], contextRef: [{
                type: Input
            }], contentList: [{
                type: ContentChildren,
                args: [TooltipContentDirective, { descendants: true }]
            }], isActivated: [{
                type: HostBinding,
                args: ['class.c11n-tooltip--activated']
            }], position: [{
                type: HostBinding,
                args: ['class']
            }], initializing: [{
                type: HostBinding,
                args: ['class.c11n-tooltip--initializing']
            }], stateChange: [{
                type: Output
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }], onWindowResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.3", ngImport: i0, type: TooltipModule, imports: [TooltipContentDirective, TooltipComponent], exports: [TooltipContentDirective, TooltipComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipModule, imports: [TooltipComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TooltipContentDirective, TooltipComponent],
                    exports: [TooltipContentDirective, TooltipComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TooltipComponent, TooltipContentDirective, TooltipModule };
